import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Scale, BookOpen, Settings, Settings2, Eye, FileSignature, Printer, FileDown, Loader2, AlertCircle, Edit, Upload } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { MarkdownViewer } from '../components/documents/markdown-viewer';
import { processLegalMarkdown } from '../lib/legal-markdown-processor';
import { TemplateConfigurator } from '../components/documents/TemplateConfigurator';
import { EnhancedFormFiller } from '../components/documents/EnhancedFormFiller';
import { compileDocument, validateDocument, generatePrintableHtml, downloadAsMarkdown, downloadAsHtml, downloadAsPdf, printDocument } from '../lib/document-compiler';
import { migrateTemplateElements } from '../lib/migrate-template-elements';
// Tab Components
import { HelpGuideTab } from '../components/documents/tabs/HelpGuideTab';
import { TemplatesTab } from '../components/documents/tabs/TemplatesTab';
import { SetupTab } from '../components/documents/tabs/SetupTab';
import { FormBuilderTab } from '../components/documents/tabs/FormBuilderTab';
import { PublishTab } from '../components/documents/tabs/PublishTab';
import { LiveTab } from '../components/documents/tabs/LiveTab';
import { FullScreenDocumentViewer } from '../components/documents/FullScreenDocumentViewer';
import { getDocumentTemplates, createDocumentInstance, updateDocumentInstance, submitDocumentForReview } from '../lib/supabase-documents';
import subscriptionAgreementTemplate from '../templates/subscription-agreement.lmd';
import ppmTemplate from '../templates/ppm.lmd';
import { OfferingSelector } from '../components/documents/OfferingSelector';
import { offeringToVariables } from '../lib/supabase-offerings';
import yaml from 'js-yaml';
/**
 * Extract YAML variables from a Legal Markdown template
 */
function extractYamlVariables(templateContent) {
    try {
        // Extract YAML front matter
        const yamlMatch = templateContent.match(/^---\s*\n([\s\S]*?)\n---/);
        if (!yamlMatch) {
            console.log('[extractYamlVariables] No YAML frontmatter found');
            return {};
        }
        // Parse YAML
        const parsed = yaml.load(yamlMatch[1]);
        if (!parsed || typeof parsed !== 'object') {
            console.log('[extractYamlVariables] YAML parsed but not an object');
            return {};
        }
        // Return the variables object from YAML
        const variables = parsed.variables || {};
        console.log('[extractYamlVariables] Successfully extracted YAML variables:', {
            hasVariables: !!parsed.variables,
            variableCount: Object.keys(variables).length
        });
        return variables;
    }
    catch (err) {
        console.error('[extractYamlVariables] Error parsing YAML:', err);
        return {};
    }
}
const templates = {
    subscription: {
        name: 'Subscription Agreement',
        template: subscriptionAgreementTemplate,
        description: 'Agreement for investors to subscribe to tokens'
    },
    ppm: {
        name: 'Private Placement Memorandum',
        template: ppmTemplate,
        description: 'Offering document describing investment terms and risks'
    }
};
export function LegalDocumentsPage() {
    const navigate = useNavigate();
    const [selectedTemplate, setSelectedTemplate] = useState('subscription');
    const [generatedMarkdown, setGeneratedMarkdown] = useState('');
    const [markdownUrl, setMarkdownUrl] = useState('');
    const [activeTab, setActiveTab] = useState('templates');
    const [fieldConfigurations, setFieldConfigurations] = useState({});
    const [formValues, setFormValues] = useState({});
    const [signatures, setSignatures] = useState({});
    const [previewRole, setPreviewRole] = useState('investor');
    const [isFullScreenView, setIsFullScreenView] = useState(false);
    // Supabase state
    const [supabaseTemplates, setSupabaseTemplates] = useState([]);
    const [currentDocumentInstance, setCurrentDocumentInstance] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [saveStatus, setSaveStatus] = useState('idle');
    // Offering selection state
    const [selectedOffering, setSelectedOffering] = useState(null);
    // Detected fields from SetupTab
    const [detectedFields, setDetectedFields] = useState([]);
    // Load templates from Supabase on mount
    useEffect(() => {
        loadTemplatesFromSupabase();
        // Expose migration function to browser console for one-time migration
        if (typeof window !== 'undefined') {
            window.migrateTemplateElements = async (forceRemigrate = false) => {
                const result = await migrateTemplateElements(forceRemigrate);
                console.log('Migration result:', result);
                // Reload templates after migration
                await loadTemplatesFromSupabase();
                alert(`Migration complete! Migrated: ${result.migrated}, Skipped: ${result.skipped}`);
            };
            console.log('💡 To migrate existing templates, run: migrateTemplateElements()');
            console.log('💡 To force re-migrate ALL templates, run: migrateTemplateElements(true)');
        }
    }, []);
    const loadTemplatesFromSupabase = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const dbTemplates = await getDocumentTemplates({ publishedOnly: false });
            setSupabaseTemplates(dbTemplates);
            // Load field configurations from templates
            const configs = {};
            dbTemplates.forEach(template => {
                configs[template.id] = template.field_configurations;
            });
            setFieldConfigurations(configs);
            // Auto-select first template if none selected
            if (dbTemplates.length > 0 && !selectedTemplate) {
                setSelectedTemplate(dbTemplates[0].id);
            }
        }
        catch (err) {
            console.error('Error loading templates:', err);
            setError('Failed to load templates from database. Using local templates.');
            // Fall back to localStorage
            const saved = localStorage.getItem('template-field-configs');
            if (saved) {
                try {
                    setFieldConfigurations(JSON.parse(saved));
                }
                catch (e) {
                    console.error('Failed to load field configurations', e);
                }
            }
        }
        finally {
            setIsLoading(false);
        }
    };
    // Save field configurations to Supabase and localStorage
    const saveFieldConfigurations = async (templateId, fields) => {
        const updated = { ...fieldConfigurations, [templateId]: fields };
        setFieldConfigurations(updated);
        localStorage.setItem('template-field-configs', JSON.stringify(updated));
        setSaveStatus('saving');
        try {
            // Find if this template exists in Supabase
            const supabaseTemplate = supabaseTemplates.find(t => t.id === templateId);
            if (supabaseTemplate) {
                // Update existing template
                const { updateDocumentTemplate } = await import('../lib/supabase-documents');
                await updateDocumentTemplate(templateId, {
                    field_configurations: fields
                });
                setSaveStatus('saved');
                setTimeout(() => setSaveStatus('idle'), 2000);
            }
            else {
                // Template doesn't exist in Supabase yet - save for when we sync
                console.log('Template not in Supabase yet, saved to localStorage');
                setSaveStatus('saved');
                setTimeout(() => setSaveStatus('idle'), 2000);
            }
        }
        catch (err) {
            console.error('Error saving field configurations:', err);
            setSaveStatus('error');
            setTimeout(() => setSaveStatus('idle'), 3000);
        }
    };
    // Handle offering selection - auto-populate form values
    const handleOfferingSelected = (offering) => {
        setSelectedOffering(offering);
        if (offering) {
            // Convert offering data to template variables
            const offeringVariables = offeringToVariables(offering);
            console.log('[LegalDocumentsPage] Auto-populating from offering:', {
                offeringId: offering.id,
                offeringName: offering.name,
                variableCount: Object.keys(offeringVariables).length,
                sampleVars: {
                    company_name: offeringVariables.company_name,
                    offering_name: offeringVariables.offering_name,
                    account_number: offeringVariables.account_number
                }
            });
            // Merge with existing form values (offering data takes precedence)
            setFormValues(prevValues => ({
                ...prevValues,
                ...offeringVariables
            }));
            // Don't auto-generate - let user click "Generate Document" button
            // This ensures state has updated and user can review values first
        }
        else {
            console.log('[LegalDocumentsPage] Offering cleared - manual entry mode');
            // Clear form values when offering is cleared
            setFormValues({});
        }
    };
    const getSelectedTemplate = () => {
        // Try to find Supabase template first
        const supabaseTemplate = supabaseTemplates.find(t => t.id === selectedTemplate);
        if (supabaseTemplate) {
            return {
                name: supabaseTemplate.name,
                template: supabaseTemplate.template_content,
                description: supabaseTemplate.description || '',
                extracted_elements: supabaseTemplate.extracted_elements
            };
        }
        // Fall back to hardcoded templates
        return templates[selectedTemplate];
    };
    const generateDocument = () => {
        const templateInfo = getSelectedTemplate();
        if (!templateInfo) {
            setError('Template not found');
            return;
        }
        const template = templateInfo.template;
        console.log('[LegalDocumentsPage] generateDocument called:', {
            formValuesCount: Object.keys(formValues).length,
            formValuesSample: Object.keys(formValues).slice(0, 10).map(k => ({ [k]: formValues[k] })),
            hasPlaceholders: Object.keys(formValues).some(k => k.startsWith('placeholder_')),
            placeholderCount: Object.keys(formValues).filter(k => k.startsWith('placeholder_')).length
        });
        // Extract YAML variables from the template file itself
        const yamlVariables = extractYamlVariables(template);
        console.log('[LegalDocumentsPage] Extracted YAML variables from template:', {
            yamlVarCount: Object.keys(yamlVariables).length,
            yamlSample: Object.keys(yamlVariables).slice(0, 5).map(k => ({ [k]: yamlVariables[k] }))
        });
        // Priority order: formValues (offering data) > YAML variables (template) > fallback defaults
        // If offering selected: use offering data
        // If no offering: use YAML from template
        // If no YAML: use minimal fallback defaults
        const fallbackDefaults = {
            company_name: '[COMPANY NAME]',
            offering_name: '[OFFERING NAME]',
            date: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
        };
        const variables = {
            ...fallbackDefaults, // Lowest priority - just placeholders
            ...yamlVariables, // Medium priority - from template YAML
            ...formValues // Highest priority - from offering selection or manual entry
        };
        console.log('[LegalDocumentsPage] Using variables for generation:', {
            source: Object.keys(formValues).length > 0 ? 'formValues' : 'defaults',
            variableCount: Object.keys(variables).length,
            variablesSample: Object.keys(variables).slice(0, 10).map(k => ({ [k]: variables[k] }))
        });
        const processed = processLegalMarkdown(template, variables);
        setGeneratedMarkdown(processed);
        console.log('[LegalDocumentsPage] Document generated:', {
            processedLength: processed.length,
            hasUnfilledPlaceholders: processed.includes('[') && processed.includes(']'),
            sampleOutput: processed.substring(0, 500)
        });
        // Create a blob URL for the markdown viewer
        const blob = new Blob([processed], { type: 'text/markdown' });
        const url = URL.createObjectURL(blob);
        setMarkdownUrl(url);
        // Open full screen view
        setIsFullScreenView(true);
    };
    const handleMoveToSetup = () => {
        setIsFullScreenView(false);
        setActiveTab('setup');
    };
    const handleFormChange = (fieldName, value) => {
        setFormValues(prev => ({ ...prev, [fieldName]: value }));
    };
    const handleSignature = (fieldName, signatureData) => {
        setSignatures(prev => ({ ...prev, [fieldName]: signatureData }));
        setFormValues(prev => ({ ...prev, [fieldName]: signatureData.data }));
    };
    const handleImageUpload = (fieldName, imageData) => {
        setFormValues(prev => ({ ...prev, [fieldName]: imageData.data }));
    };
    const handleCompileAndPreview = () => {
        const templateInfo = getSelectedTemplate();
        if (!templateInfo) {
            setError('Template not found');
            return;
        }
        const template = templateInfo.template;
        const documentInstance = {
            id: 'preview',
            templateId: selectedTemplate,
            templateVersion: '1.0',
            values: formValues,
            signatures,
            status: 'draft',
            createdAt: new Date(),
            updatedAt: new Date()
        };
        const compiled = compileDocument(template, documentInstance, currentFields, {
            includeSignatures: true,
            format: 'markdown'
        });
        setGeneratedMarkdown(compiled.content);
        const blob = new Blob([compiled.content], { type: 'text/markdown' });
        const url = URL.createObjectURL(blob);
        setMarkdownUrl(url);
    };
    const handleDownloadMarkdown = () => {
        const filename = `${selectedTemplate}-${Date.now()}`;
        downloadAsMarkdown(generatedMarkdown, filename);
    };
    const handleDownloadHtml = () => {
        const templateInfo = getSelectedTemplate();
        if (!templateInfo)
            return;
        const template = templateInfo.template;
        const documentInstance = {
            id: 'download',
            templateId: selectedTemplate,
            templateVersion: '1.0',
            values: formValues,
            signatures,
            status: 'draft',
            createdAt: new Date(),
            updatedAt: new Date()
        };
        const compiled = compileDocument(template, documentInstance, currentFields, {
            includeSignatures: true,
            format: 'html'
        });
        const printableHtml = generatePrintableHtml(compiled.content, templateInfo.name, false);
        const filename = `${selectedTemplate}-${Date.now()}`;
        downloadAsHtml(printableHtml, filename);
    };
    const handleDownloadPdf = async () => {
        try {
            const templateInfo = getSelectedTemplate();
            if (!templateInfo)
                return;
            const template = templateInfo.template;
            const documentInstance = {
                id: 'download-pdf',
                templateId: selectedTemplate,
                templateVersion: '1.0',
                values: formValues,
                signatures,
                status: 'draft',
                createdAt: new Date(),
                updatedAt: new Date()
            };
            const compiled = compileDocument(template, documentInstance, currentFields, {
                includeSignatures: true,
                format: 'html'
            });
            const printableHtml = generatePrintableHtml(compiled.content, templateInfo.name, false);
            const filename = `${selectedTemplate}-${Date.now()}`;
            await downloadAsPdf(printableHtml, filename);
        }
        catch (error) {
            console.error('PDF download failed:', error);
            alert(error instanceof Error ? error.message : 'Failed to download PDF');
        }
    };
    const handlePrint = () => {
        const templateInfo = getSelectedTemplate();
        if (!templateInfo)
            return;
        const template = templateInfo.template;
        const documentInstance = {
            id: 'print',
            templateId: selectedTemplate,
            templateVersion: '1.0',
            values: formValues,
            signatures,
            status: 'draft',
            createdAt: new Date(),
            updatedAt: new Date()
        };
        const compiled = compileDocument(template, documentInstance, currentFields, {
            includeSignatures: true,
            format: 'html'
        });
        const printableHtml = generatePrintableHtml(compiled.content, templateInfo.name, false);
        printDocument(printableHtml);
    };
    const handleValidateAndSubmit = async () => {
        const documentInstance = {
            id: currentDocumentInstance?.id || 'preview',
            templateId: selectedTemplate,
            templateVersion: '1.0',
            values: formValues,
            signatures,
            status: 'draft',
            createdAt: new Date(),
            updatedAt: new Date()
        };
        const validation = validateDocument(documentInstance, currentFields);
        if (!validation.valid) {
            const missingFieldNames = validation.missingFields.map(f => f.label).join(', ');
            alert(`Please fill in all required fields: ${missingFieldNames}`);
            return;
        }
        setSaveStatus('saving');
        try {
            if (currentDocumentInstance) {
                // Update existing document
                await updateDocumentInstance(currentDocumentInstance.id, {
                    fieldValues: formValues,
                    signatures,
                    status: 'pending_compliance'
                });
                // Submit for review
                await submitDocumentForReview(currentDocumentInstance.id);
                alert('Document validated and submitted for review!');
                setSaveStatus('saved');
            }
            else {
                // Create new document instance
                const newDoc = await createDocumentInstance({
                    templateId: selectedTemplate,
                    templateVersion: '1.0',
                    fieldValues: formValues
                });
                setCurrentDocumentInstance(newDoc);
                // Submit for review
                await submitDocumentForReview(newDoc.id);
                alert('Document created and submitted for review!');
                setSaveStatus('saved');
            }
            handleCompileAndPreview();
            setTimeout(() => setSaveStatus('idle'), 2000);
        }
        catch (err) {
            console.error('Error saving document:', err);
            setError('Failed to save document. Please try again.');
            setSaveStatus('error');
            setTimeout(() => setSaveStatus('idle'), 3000);
        }
    };
    const currentFields = fieldConfigurations[selectedTemplate] || [];
    const downloadDocument = () => {
        const blob = new Blob([generatedMarkdown], { type: 'text/markdown' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${selectedTemplate}-${Date.now()}.md`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };
    return (_jsxs("div", { className: "flex flex-col h-full", style: { backgroundColor: 'var(--bg-base)' }, children: [_jsxs("div", { className: "flex items-center justify-between px-8 py-6", style: { borderBottom: '1px solid var(--border-color)' }, children: [_jsxs("div", { children: [_jsxs("div", { className: "flex items-center gap-3 mb-2", children: [_jsx("div", { className: "w-12 h-12 rounded-2xl flex items-center justify-center", style: { backgroundColor: '#D9770620' }, children: isLoading ? (_jsx(Loader2, { className: "w-6 h-6 animate-spin", style: { color: '#D97706' } })) : (_jsx(Scale, { className: "w-6 h-6", style: { color: '#D97706' } })) }), _jsxs("div", { children: [_jsx("h1", { className: "text-3xl font-light", style: { color: 'var(--text-primary)' }, children: "Legal Documents" }), saveStatus === 'saving' && (_jsxs("p", { className: "text-xs flex items-center gap-2 mt-1", style: { color: '#D97706' }, children: [_jsx(Loader2, { className: "w-3 h-3 animate-spin" }), "Saving to database..."] })), saveStatus === 'saved' && (_jsx("p", { className: "text-xs flex items-center gap-2 mt-1", style: { color: '#10b981' }, children: "\u2713 Saved successfully" })), saveStatus === 'error' && (_jsxs("p", { className: "text-xs flex items-center gap-2 mt-1", style: { color: '#ef4444' }, children: [_jsx(AlertCircle, { className: "w-3 h-3" }), "Save failed"] }))] })] }), _jsx("p", { className: "text-sm", style: { color: 'var(--text-secondary)' }, children: "Complete document workflow from templates to live publishing" }), error && (_jsxs("div", { className: "mt-2 px-3 py-2 rounded-lg flex items-center gap-2", style: { backgroundColor: '#fef2f2', border: '1px solid #fecaca' }, children: [_jsx(AlertCircle, { className: "w-4 h-4", style: { color: '#ef4444' } }), _jsx("p", { className: "text-xs", style: { color: '#b91c1c' }, children: error })] }))] }), _jsxs("div", { className: "flex gap-1 p-1 rounded-lg", style: { backgroundColor: 'var(--bg-elevated)' }, children: [_jsxs("button", { onClick: () => setActiveTab('help'), className: "px-4 py-2 rounded-md transition-all text-sm font-medium", style: {
                                    backgroundColor: activeTab === 'help' ? '#D97706' : 'transparent',
                                    color: activeTab === 'help' ? 'white' : 'var(--text-secondary)'
                                }, children: [_jsx(BookOpen, { className: "w-4 h-4 inline-block mr-2" }), "Help Guide"] }), _jsxs("button", { onClick: () => setActiveTab('templates'), className: "px-4 py-2 rounded-md transition-all text-sm font-medium", style: {
                                    backgroundColor: activeTab === 'templates' ? '#D97706' : 'transparent',
                                    color: activeTab === 'templates' ? 'white' : 'var(--text-secondary)'
                                }, children: [_jsx(FileText, { className: "w-4 h-4 inline-block mr-2" }), "Templates"] }), _jsxs("button", { onClick: () => setActiveTab('configure'), className: "px-4 py-2 rounded-md transition-all text-sm font-medium", style: {
                                    backgroundColor: activeTab === 'configure' ? '#D97706' : 'transparent',
                                    color: activeTab === 'configure' ? 'white' : 'var(--text-secondary)'
                                }, children: [_jsx(Settings, { className: "w-4 h-4 inline-block mr-2" }), "Configure"] }), _jsxs("button", { onClick: () => setActiveTab('generate'), className: "px-4 py-2 rounded-md transition-all text-sm font-medium", style: {
                                    backgroundColor: activeTab === 'generate' ? '#D97706' : 'transparent',
                                    color: activeTab === 'generate' ? 'white' : 'var(--text-secondary)'
                                }, children: [_jsx(FileSignature, { className: "w-4 h-4 inline-block mr-2" }), "Generate"] }), _jsxs("button", { onClick: () => setActiveTab('setup'), className: "px-4 py-2 rounded-md transition-all text-sm font-medium", style: {
                                    backgroundColor: activeTab === 'setup' ? '#D97706' : 'transparent',
                                    color: activeTab === 'setup' ? 'white' : 'var(--text-secondary)'
                                }, children: [_jsx(Edit, { className: "w-4 h-4 inline-block mr-2" }), "Setup"] }), _jsxs("button", { onClick: () => setActiveTab('formbuilder'), className: "px-4 py-2 rounded-md transition-all text-sm font-medium", style: {
                                    backgroundColor: activeTab === 'formbuilder' ? '#D97706' : 'transparent',
                                    color: activeTab === 'formbuilder' ? 'white' : 'var(--text-secondary)'
                                }, children: [_jsx(Settings2, { className: "w-4 h-4 inline-block mr-2" }), "Form Builder"] }), _jsxs("button", { onClick: () => setActiveTab('publish'), className: "px-4 py-2 rounded-md transition-all text-sm font-medium", style: {
                                    backgroundColor: activeTab === 'publish' ? '#D97706' : 'transparent',
                                    color: activeTab === 'publish' ? 'white' : 'var(--text-secondary)'
                                }, children: [_jsx(Upload, { className: "w-4 h-4 inline-block mr-2" }), "Publish"] }), _jsxs("button", { onClick: () => setActiveTab('live'), className: "px-4 py-2 rounded-md transition-all text-sm font-medium", style: {
                                    backgroundColor: activeTab === 'live' ? '#D97706' : 'transparent',
                                    color: activeTab === 'live' ? 'white' : 'var(--text-secondary)'
                                }, children: [_jsx(Eye, { className: "w-4 h-4 inline-block mr-2" }), "Live"] })] })] }), _jsx("div", { className: "flex-1 overflow-y-auto px-8 py-6", children: _jsxs("div", { className: "max-w-7xl mx-auto", children: [activeTab === 'help' && _jsx(HelpGuideTab, {}), activeTab === 'templates' && (_jsx(TemplatesTab, { onSelectTemplate: (id) => {
                                setSelectedTemplate(id);
                                setActiveTab('configure');
                            }, selectedTemplate: selectedTemplate })), activeTab === 'configure' && (_jsx("div", { className: "rounded-2xl p-6", style: {
                                backgroundColor: 'var(--bg-elevated)',
                                border: '1px solid var(--border-color)'
                            }, children: _jsx(TemplateConfigurator, { templateContent: getSelectedTemplate()?.template || '', existingFields: currentFields, onChange: (fields) => saveFieldConfigurations(selectedTemplate, fields), onYamlChange: (yamlConfig) => {
                                    // Update form values with YAML defaults
                                    if (yamlConfig?.variables) {
                                        console.log('[LegalDocumentsPage] Received YAML variables:', yamlConfig.variables);
                                        setFormValues(prev => ({ ...yamlConfig.variables, ...prev }));
                                    }
                                }, onValuesChange: (values) => {
                                    // Update form values with uploaded configuration and manual values
                                    console.log('[LegalDocumentsPage] Received values from TemplateConfigurator:', {
                                        count: Object.keys(values).length,
                                        sampleKeys: Object.keys(values).slice(0, 5)
                                    });
                                    setFormValues(prev => ({ ...prev, ...values }));
                                }, extractedElements: getSelectedTemplate()?.extracted_elements }) })), activeTab === 'generate' && (_jsxs("div", { className: "space-y-6", children: [_jsxs("div", { className: "rounded-2xl p-6", style: {
                                        backgroundColor: 'var(--bg-elevated)',
                                        border: '1px solid var(--border-color)'
                                    }, children: [_jsx("h3", { className: "text-xl font-medium mb-4", style: { color: 'var(--text-primary)' }, children: "Generate Document" }), _jsx(OfferingSelector, { selectedOfferingId: selectedOffering?.id, onOfferingSelected: handleOfferingSelected, className: "mb-6" }), _jsx(EnhancedFormFiller, { fields: currentFields, values: formValues, onChange: (values) => setFormValues({ ...formValues, ...values }), onSignature: (fieldName, signatureData) => {
                                                setSignatures({ ...signatures, [fieldName]: signatureData });
                                            }, previewRole: previewRole }), _jsx("div", { className: "flex gap-3 mt-6", children: _jsxs(Button, { onClick: generateDocument, style: {
                                                    backgroundColor: '#D97706',
                                                    color: 'white'
                                                }, children: [_jsx(FileText, { className: "mr-2 w-4 h-4" }), "Generate Document"] }) })] }), generatedMarkdown && markdownUrl && (_jsxs(_Fragment, { children: [_jsxs("div", { className: "rounded-2xl overflow-hidden", style: {
                                                backgroundColor: 'var(--bg-elevated)',
                                                border: '1px solid var(--border-color)'
                                            }, children: [_jsx("div", { className: "px-6 py-4", style: { borderBottom: '1px solid var(--border-color)' }, children: _jsx("h3", { className: "text-xl font-medium", style: { color: 'var(--text-primary)' }, children: "Document Preview" }) }), _jsx("div", { className: "max-h-[600px] overflow-y-auto", children: _jsx(MarkdownViewer, { url: markdownUrl }) })] }), _jsxs("div", { className: "rounded-2xl p-6", style: {
                                                backgroundColor: 'var(--bg-elevated)',
                                                border: '1px solid var(--border-color)'
                                            }, children: [_jsx("h3", { className: "text-lg font-medium mb-4", style: { color: 'var(--text-primary)' }, children: "Download Document" }), _jsxs("div", { className: "flex flex-wrap gap-3", children: [_jsxs(Button, { onClick: handleDownloadMarkdown, style: {
                                                                backgroundColor: '#10B981',
                                                                color: 'white'
                                                            }, children: [_jsx(FileDown, { className: "mr-2 w-4 h-4" }), "Download Markdown"] }), _jsxs(Button, { onClick: handleDownloadHtml, style: {
                                                                backgroundColor: '#3B82F6',
                                                                color: 'white'
                                                            }, children: [_jsx(FileDown, { className: "mr-2 w-4 h-4" }), "Download HTML"] }), _jsxs(Button, { onClick: handleDownloadPdf, style: {
                                                                backgroundColor: '#EF4444',
                                                                color: 'white'
                                                            }, children: [_jsx(FileDown, { className: "mr-2 w-4 h-4" }), "Download PDF"] }), _jsxs(Button, { onClick: handlePrint, style: {
                                                                backgroundColor: '#8B5CF6',
                                                                color: 'white'
                                                            }, children: [_jsx(Printer, { className: "mr-2 w-4 h-4" }), "Print"] })] })] })] }))] })), activeTab === 'setup' && (_jsx(SetupTab, { templateContent: getSelectedTemplate()?.template || '', onFieldsDetected: (fields) => setDetectedFields(fields) })), activeTab === 'formbuilder' && (_jsx(FormBuilderTab, { templateId: selectedTemplate, templateContent: getSelectedTemplate()?.template || '', detectedFields: detectedFields, generatedMarkdown: generatedMarkdown })), activeTab === 'publish' && (_jsx(PublishTab, { templateId: selectedTemplate, documentInstanceId: currentDocumentInstance?.id, generatedMarkdown: generatedMarkdown, templateName: getSelectedTemplate()?.name || 'Document', onPreview: () => setIsFullScreenView(true) })), activeTab === 'live' && _jsx(LiveTab, {})] }) }), isFullScreenView && generatedMarkdown && markdownUrl && (_jsx(FullScreenDocumentViewer, { markdownUrl: markdownUrl, generatedMarkdown: generatedMarkdown, onClose: () => setIsFullScreenView(false), onMoveToSetup: handleMoveToSetup, documentTitle: getSelectedTemplate()?.name || 'Generated Document' }))] }));
}
